import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, Camera, Building2, Home as HomeIcon, ArrowRight, Phone, Check } from "lucide-react";
import Seo from "@/components/Seo";
import CtaBand from "@/components/CtaBand";
import { SITE } from "@/data/site";
import MediaPlaceholder from "@/components/MediaPlaceholder";
import HeroBackdrop from "@/components/HeroBackdrop";

const APPLICATIONS = [
  {
    Icon: HomeIcon,
    title: "Residential applications",
    body: "Security cameras and camera/security-light units installed and wired cleanly for homes, with dependable power.",
  },
  {
    Icon: Building2,
    title: "Commercial applications",
    body: "Camera and security-light installation for businesses, tied into reliable, dedicated power.",
  },
];

const CAPABILITIES = [
  "Security camera installation",
  "Camera & security-light installation",
  "CCTV / surveillance camera systems",
  "Clean, professional wiring",
  "Dedicated power for camera systems",
  "Residential & commercial",
];

export default function CctvSecurity() {
  return (
    <>
      <Seo
        title="CCTV & Security Camera Installation"
        description="Kensmarc Electric installs CCTV and security cameras for homes and businesses across Los Angeles County — professional wiring from a licensed C-10 electrical contractor."
        path="/cctv-security"
      />

      <section className="relative overflow-hidden border-b border-[#1a1a22] bg-[#0b0b0f]">
        <HeroBackdrop />
        <div className="container-kx relative py-16 sm:py-20 lg:py-28">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-3xl">
            <span className="kx-badge"><ShieldCheck className="h-3.5 w-3.5" /> Security Services</span>
            <h1 className="mt-4 font-display text-4xl font-extrabold uppercase leading-tight tracking-tight text-white sm:text-5xl lg:text-6xl">
              CCTV & Security Camera Installation
            </h1>
            <p className="mt-5 max-w-2xl text-lg leading-relaxed text-neutral-300">
              Kensmarc Electric also provides surveillance and security camera installation. Because we're a
              licensed electrical contractor first, your cameras are backed by clean wiring and dependable power.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link to="/request-a-quote" data-testid="cctv-quote-btn" className="inline-flex items-center justify-center gap-2 rounded-full bg-amber-400 px-7 py-3.5 text-sm font-bold uppercase tracking-wide text-[#0A0A0C] hover:bg-amber-300">
                Request a Quote <ArrowRight className="h-4 w-4" />
              </Link>
              <a href={SITE.phonePrimary.tel} data-testid="cctv-call-btn" className="inline-flex items-center justify-center gap-2 rounded-full border border-[#2e2e39] px-7 py-3.5 text-sm font-bold text-white hover:border-amber-400/60">
                <Phone className="h-4 w-4 text-amber-400" /> Call {SITE.phonePrimary.display}
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="section-pad">
        <div className="container-kx grid gap-12 lg:grid-cols-2 lg:items-center">
          <div>
            <span className="eyebrow"><span className="h-px w-6 bg-amber-400" /> What we install</span>
            <h2 className="mt-3 font-display text-3xl font-bold uppercase text-white sm:text-4xl">Surveillance done by electricians</h2>
            <p className="mt-4 leading-relaxed text-neutral-400">
              Kensmarc Electric mounts and wires security cameras and camera/security-light units — handling
              the wiring and dedicated power so your system looks clean and runs reliably.
            </p>
            <ul className="mt-6 grid gap-3 sm:grid-cols-2">
              {CAPABILITIES.map((c) => (
                <li key={c} className="flex items-start gap-3">
                  <Check className="mt-0.5 h-5 w-5 shrink-0 text-amber-400" />
                  <span className="text-sm text-neutral-200">{c}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <MediaPlaceholder slot="cctv-1" Icon={Camera} alt="Kensmarc Electric exterior security camera and light installation" label="CCTV Project Photo Coming Soon" className="min-h-[420px] lg:min-h-[560px]" testId="cctv-media-slot-1" />
            <p className="mt-3 text-xs text-neutral-500">Real Kensmarc Electric camera &amp; security-light installation.</p>
          </div>
        </div>
      </section>

      <section className="border-y border-[#1a1a22] bg-[#0c0c11] py-16 lg:py-20">
        <div className="container-kx grid gap-6 md:grid-cols-2">
          {APPLICATIONS.map(({ Icon, title, body }) => (
            <div key={title} className="kx-card p-8">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-amber-400/10 text-amber-400">
                <Icon className="h-6 w-6" />
              </div>
              <h3 className="mt-5 font-display text-xl font-bold uppercase text-white">{title}</h3>
              <p className="mt-2 leading-relaxed text-neutral-400">{body}</p>
            </div>
          ))}
        </div>
        <p className="container-kx mt-8 flex items-center gap-2 text-sm text-neutral-500">
          <Camera className="h-4 w-4 text-amber-400" />
          Have specific camera or system requirements? Tell us in your quote request and we'll review them with you.
        </p>
      </section>

      <CtaBand title="Secure your property" subtitle="Request a quote for CCTV or security camera installation and we'll follow up to plan your system." />
    </>
  );
}
